import { useMemo, useState } from 'react';
import { useRollNumbers, useRollNumbersAssign } from '../../../api/hooks';
import { SchoolApi } from '../../../api/client';
import type { RollNumberRow } from '../../../api/types';
import { Modal, Button, StatStrip, SortableTh, Term, Spinner, ErrorPanel, PillTabs } from '../../../components/ui';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { apiErrorText } from '../../../lib/apiError';
import { formatRoll } from '../../../lib/roll';

const GENERIC_ERROR = 'Could not assign roll numbers. Try again.';

function rosterFilename(gradeName: string): string {
  const slug = gradeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'grade';
  return `roll-numbers-${slug}.pdf`;
}

interface Props {
  gradeId: number;
  gradeName: string;
  isOpen: boolean;
  onClose: () => void;
  /** Lets the dialog switch grade (the paper exams header opens it without a grade in mind). */
  grades?: Array<{ id: number; name: string }>;
  onGradeChange?: (gradeId: number) => void;
}

/** Setup → Classes: per-grade roll number assignment and roster download. */
export default function RollNumbersModal({ gradeId, gradeName, isOpen, onClose, grades, onGradeChange }: Props) {
  const { data, isLoading, isError, refetch } = useRollNumbers(isOpen ? gradeId : null);
  const assign = useRollNumbersAssign();
  const [assignError, setAssignError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const students = data?.students ?? [];
  const columns = useMemo<SortColumn<RollNumberRow>[]>(() => [
    { key: 'roll', accessor: (r) => r.roll_number, type: 'number' },
    { key: 'name', accessor: (r) => r.name },
    { key: 'section', accessor: (r) => r.section },
  ], []);
  const { rows, sort, toggle } = useSortableTable(students, columns, { key: 'roll', dir: 'asc' });

  async function handleAssign() {
    setAssignError(null);
    try {
      await assign.mutateAsync(gradeId);
    } catch (err) {
      setAssignError(apiErrorText(err, GENERIC_ERROR));
    }
  }

  async function handleDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      await SchoolApi.downloadRollRoster(gradeId, rosterFilename(gradeName));
    } catch (err) {
      setDownloadError(apiErrorText(err, 'Could not download the roster. Try again.'));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={<Term k="roll_number">Roll numbers — {gradeName}</Term>}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleDownload} loading={downloading}>
            Roster (PDF)
          </Button>
          <Button
            type="button"
            onClick={handleAssign}
            disabled={(data?.unassigned ?? 0) === 0 || assign.isPending}
            loading={assign.isPending}>
            Assign missing
          </Button>
        </div>
      }>
      <div className="space-y-4">
        {grades && grades.length > 1 && onGradeChange && (
          <PillTabs<string>
            label="Grade" scroll dense value={String(gradeId)} onChange={(k) => onGradeChange(Number(k))}
            options={grades.map((g) => ({ key: String(g.id), label: g.name }))} />
        )}
        <p className="text-sm text-body dark:text-bodydark">
          Students bubble this number on every answer sheet. Numbers are assigned once, alphabetically, and never change.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner size="md" /></div>
        ) : isError ? (
          <ErrorPanel title="Could not load roll numbers" onRetry={() => refetch()} />
        ) : (
          <>
            <StatStrip
              columns={3}
              boxed
              items={[
                { label: 'Students', value: data?.count ?? 0 },
                { label: 'Assigned', value: (data?.count ?? 0) - (data?.unassigned ?? 0) },
                { label: 'Unassigned', value: data?.unassigned ?? 0, tone: (data?.unassigned ?? 0) > 0 ? 'warning' : undefined },
              ]}
            />
            {assignError && <p className="text-xs text-danger">{assignError}</p>}
            {downloadError && <p className="text-xs text-danger">{downloadError}</p>}

            <div className="max-h-80 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-bodydark2">
                    <SortableTh columnKey="roll" sort={sort} onToggle={toggle} help="roll_number">Roll</SortableTh>
                    <SortableTh columnKey="name" sort={sort} onToggle={toggle}>Name</SortableTh>
                    <SortableTh columnKey="section" sort={sort} onToggle={toggle}>Section</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.student_id} className="border-t border-stroke dark:border-strokedark">
                      <td className="px-3 py-2">
                        {r.roll_number != null ? formatRoll(r.roll_number) : (
                          <span className="text-bodydark2">Unassigned</span>
                        )}
                      </td>
                      <td className="px-3 py-2">{r.name}</td>
                      <td className="px-3 py-2">{r.section ?? '—'}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-6 text-center text-bodydark2">No students in this grade.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
